import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { map, take, shareReplay, tap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

// Define types for environment and parameter objects
export interface Environment {
  APIURL: string;
  sla:string;
  local: string;
  localURL: string;
  sso:string
  SSOURL:string
  hiringUrl:string;
  SERURL:string;
  serv:string;
}

export interface QueryParams {
  [key: string]: string | number | boolean | null | undefined;
}

export const ENVIRONMENT_TOKEN = new InjectionToken<Environment>('environment');

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  private baseUrls: Map<string, string> = new Map();

  constructor(
    @Inject(ENVIRONMENT_TOKEN) private environment: Environment,
    private http: HttpClient
  ) {
    this.baseUrls.set('serv', environment.SERURL);
    this.baseUrls.set('apa', environment.APIURL);
    this.baseUrls.set(environment.local, environment.localURL);
    this.baseUrls.set('sso', environment.SSOURL);
    this.baseUrls.set('hiring', environment.hiringUrl);
  }

  // Helper Methods
  //-------------------------------------------------------------------------------------------
  private buildQueryParams(params?: QueryParams): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        const value = params[key];
        if (value !== null && value !== undefined) {
          httpParams = httpParams.append(key, value.toString());
        }
      });
    }
    return httpParams;
  }

  private buildUrl(baseUrlKey: string, apiName: string, params?: QueryParams): string {
    const baseUrl = this.baseUrls.get(baseUrlKey) || '';
    const queryParams = this.buildQueryParams(params);
    return `${baseUrl}${apiName}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  }

  private handleResponse<T>(observable: Observable<T>): Observable<T> {
    return observable.pipe(
      take(1),
      map((response) => response)
    );
  }

  // TanStack Query Integration
  //-------------------------------------------------------------------------------------------


  // API Methods
  //------------------------------------------------------------------------------------------------
  get<T>(baseUrlKey: string, apiName: string, params?: QueryParams): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName, params);
    return new Observable<T>(observer => {
      this.http.get<T>(url,  ).pipe(
          take(1)
        ).subscribe({
          next: (response) => observer.next(response),
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });
    });
  }

  search<T>(baseUrlKey: string, apiName: string, params?: QueryParams, body?: T): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.post<T>(url, body,  ));
  }

  fetchGet<T>(
    baseUrlKey: string,
    apiName: string,
    params?: QueryParams,
    options?: { headers?: HttpHeaders; observe?: 'response' }
  ): Observable<HttpResponse<T>> {
    const url = this.buildUrl(baseUrlKey, apiName, params);
    return this.http.get<T>(url, { ...options, observe: 'response', withCredentials: true }).pipe(take(1));
  }

  fetchPost<T>(
    baseUrlKey: string,
    apiName: string,
    params?: QueryParams,
    body?: T,
    options?: { headers?: HttpHeaders; observe?: 'response' }
  ): Observable<HttpResponse<T>> {
    const url = this.buildUrl(baseUrlKey, apiName, params);
    return this.http.post<T>(url, body, { ...options, observe: 'response', withCredentials: true }).pipe(take(1));
  }

  post<T>(baseUrlKey: string, apiName: string, body?: T): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName);
    return new Observable<T>(observer => {
      this.http.post<T>(url, body,  ).pipe(
          take(1)
        ).subscribe({
          next: (response) => observer.next(response),
          error: (error) => observer.error(error),
          complete: () => observer.complete()
        });
    });
  }
  postwithHeader<T>(baseUrlKey: string, apiName: string,body?: T,params?: QueryParams, options?: { headers?: HttpHeaders; }): Observable<T> {
    const url = this.buildUrlWithIdonly(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.post<T>(url,body,{ ...options } ));
  }
  put<T>(baseUrlKey: string, apiName: string, body?: T): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName);
    return this.handleResponse<T>(this.http.put<T>(url, body,  ));
  }
  PutParamter<T>(baseUrlKey: string, apiName: string, params?: QueryParams  , body?: T): Observable<T> {
    const url = this.buildUrlWithIdonly(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.put<T>(url,body,  ));
  }
  delete<T>(baseUrlKey: string, apiName: string, params?: QueryParams , body?: T): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.delete<T>(url,{body:body, withCredentials: true}));
  }

  patch<T>(baseUrlKey: string, apiName: string, params?: QueryParams, body?: T): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.patch<T>(url, body,  ));
  }

  deleteParamter<T>(baseUrlKey: string, apiName: string, params?: QueryParams): Observable<T> {
    const url = this.buildUrl(baseUrlKey, apiName,params);
    return this.handleResponse<T>(this.http.delete<T>(url,  ));
  }
  deleteParamterAsPut<T>(baseUrlKey: string, apiName: string, params?: QueryParams): Observable<T> {
    const url = this.buildUrlWithIdonly(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.put<T>(url,{},  ));
  }
  postParamter<T>(baseUrlKey: string, apiName: string,params?: QueryParams, options?: { headers?: HttpHeaders; }): Observable<T> {
    const url = this.buildUrlWithIdonly(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.post<T>(url,{ ...options,   } ));
  }
  getParamter<T>(baseUrlKey: string, apiName: string,id: string,params?: QueryParams): Observable<T> {
    const url = this.buildUrlWithId(baseUrlKey, apiName, id , params);

    return this.handleResponse<T>(this.http.get<T>(url,  ));
  }
  getwithParamter<T>(baseUrlKey: string, apiName: string,params?: QueryParams, options?: { headers?: HttpHeaders; }): Observable<T> {
    const url = this.buildUrlWithIdonly(baseUrlKey, apiName, params);
    return this.handleResponse<T>(this.http.get<T>(url,{ ...options, withCredentials: true } ));
  }

  private buildUrlWithId(baseUrlKey: string, apiName: string, id: string,params?: QueryParams): string {
    const baseUrl = this.baseUrls.get(baseUrlKey) || '';// Replace with actual base URL logic if dynamic
    const queryParams = this.buildQueryParams(params);

    return `${baseUrl}${apiName}/${id}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  }
  private buildUrlWithIdonly(baseUrlKey: string, apiName: string,params?: QueryParams): string {
    const baseUrl = this.baseUrls.get(baseUrlKey) || ''; // Replace with actual base URL logic if dynamic
    const queryParams = this.buildQueryParams(params);

    return `${baseUrl}${apiName}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
  }

  clearCache(): void {
    // Cache removal - this method is kept for compatibility but does nothing
  }
}
